import React from 'react';
import {getCountriesService, getStatesService} from '../services/geo.service';
// Deprecated local list — used only until the API answers, or when it cannot be reached.
import {countries as LEGACY_COUNTRIES} from './countries';

/*
 * Countries and states from the API's geo master (GET /v1/meta/countries…) so every DQ
 * app shows the same list. Synchronous on purpose, like the old `countries` import:
 *   getCountries()              [{ code, label, name, dial_code, has_states }]
 *   getStatesByCountry(code)    [{ code, name, category, parent }] — [] until loaded
 *   getStatesStatus(code)       'loading' | 'loaded' | 'failed' | 'none'
 * Before the API answers, countries come from the deprecated local list (admin has no
 * local states). Components that call `useGeo()` re-render when a list arrives.
 *
 * Save codes, never names: a country is its ISO 3166-1 alpha-2 code, a state its
 * ISO 3166-2 code.
 */

const toEntry = (c) => ({code: c.code, label: c.name, name: c.name, dial_code: c.dial_code || null, has_states: !!c.has_states});

let countries = LEGACY_COUNTRIES.map((c) => toEntry({code: c.code, name: c.label}));
let byCode = new Map(countries.map((c) => [c.code, c]));
// Names older records may hold instead of a code.
const LEGACY_LABEL_TO_CODE = new Map(LEGACY_COUNTRIES.map((c) => [c.label.toLowerCase(), c.code]));

let countriesPromise = null;
const statesByCountry = new Map(); // code → states from the API
const statesStatus = new Map();    // code → 'loading' | 'loaded' | 'failed'
let revision = 0;
const listeners = new Set();
const notify = () => { revision += 1; listeners.forEach((l) => l(revision)); };

const upper = (v) => String(v || '').trim().toUpperCase();

/** Fetches the country list once per page load. Safe to call repeatedly. */
export const loadCountries = () => {
    if (countriesPromise) return countriesPromise;
    countriesPromise = getCountriesService()
        .then((res) => {
            const list = res && res.data && res.data.countries;
            if (!Array.isArray(list) || !list.length) return;
            countries = list.map(toEntry);
            byCode = new Map(countries.map((c) => [c.code, c]));
            notify();
        })
        .catch(() => { /* keep the local list for this page load */ });
    return countriesPromise;
};

/** The current country list — the API's once loaded, the deprecated local one before. */
export const getCountries = () => countries;

/** Display name for a code; '' when unknown. */
export const getCountryLabel = (code) => (byCode.get(upper(code)) || {}).label || '';

/** The country for a stored value that may be a code or (older records) a name. */
export const findCountry = (codeOrName) => {
    const raw = String(codeOrName || '').trim();
    if (!raw) return undefined;
    const direct = byCode.get(raw.toUpperCase());
    if (direct) return direct;
    const lower = raw.toLowerCase();
    return countries.find((c) => c.label.toLowerCase() === lower)
        || byCode.get(LEGACY_LABEL_TO_CODE.get(lower));
};

/** How a stored country value reads: its name for a code, the value itself otherwise. */
export const displayCountry = (value) => (findCountry(value) || {}).label || value || '';

const ensureStates = (cc) => {
    if (statesStatus.has(cc)) return;
    statesStatus.set(cc, 'loading');
    getStatesService(cc)
        .then((res) => {
            const list = res && res.data && res.data.states;
            statesByCountry.set(cc, Array.isArray(list) ? list : []);
            statesStatus.set(cc, 'loaded');
        })
        // Not retried on every render: the form falls back to typing for this page load.
        .catch(() => { statesStatus.set(cc, 'failed'); })
        .finally(notify);
};

/** A country's states; starts loading them from the API on first use ([] until then). */
export const getStatesByCountry = (countryCode) => {
    const cc = upper(countryCode);
    if (!byCode.has(cc)) return [];
    ensureStates(cc);
    return statesByCountry.get(cc) || [];
};

/** 'none' (no or unknown country), 'loading', 'loaded' or 'failed'. */
export const getStatesStatus = (countryCode) => {
    const cc = upper(countryCode);
    if (!byCode.has(cc)) return 'none';
    ensureStates(cc);
    return statesStatus.get(cc);
};

/** How a stored state value reads: its name for an ISO 3166-2 code ('SG-01'), the value itself otherwise. */
export const displayState = (value) => {
    const raw = String(value || '').trim();
    const m = /^([A-Za-z]{2})-[A-Za-z0-9]+$/.exec(raw);
    if (!m) return raw;
    const hit = getStatesByCountry(m[1]).find((s) => s.code === raw.toUpperCase());
    return hit ? hit.name : raw;
};

/** Loads the list on mount and re-renders the component when the API's list arrives. */
export const useGeo = () => {
    const [, setRevision] = React.useState(revision);
    React.useEffect(() => {
        listeners.add(setRevision);
        loadCountries();
        setRevision(revision);
        return () => { listeners.delete(setRevision); };
    }, []);
    return {countries, getCountryLabel, findCountry, displayCountry, getStatesByCountry, getStatesStatus, displayState};
};
